import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs, type Appointment, type Client, type Consultant, type Paged } from '../api';
import { ErrorAlert, Loading, Pending } from '../components/ui';
import { hm, MODALITY_LABEL, money, monthLabel, PROJECT_STATUS_LABEL } from '../format';
import { Link } from 'react-router-dom';
import { AppointmentItem } from './Agenda';

interface CompanyOverview {
  clients: { active?: number; inactive?: number };
  projectsByStatus: Partial<Record<keyof typeof PROJECT_STATUS_LABEL, number>>;
  projectsByOwner: { consultantId: string; name: string; n: number }[];
  activeContractsByModality: Partial<Record<keyof typeof MODALITY_LABEL, number>>;
  production: { month: string; byExecutor: { consultantId: string; name: string; minutes: number; sessions: number; clientsServed: number }[] };
  openDrafts: number;
  agendaWeek: Appointment[];
  balances: { id: string; code: string; clientName: string; projectName: string; contractedMinutes: number | null; lowBalanceMinutes: number | null; balance: number; consumed: number; reversed: number; credits: number; pendingOverageMinutes: number; hasInitialCredit: boolean }[];
  billables: { billingStatus: string; transportStatus: string; reviewStatus: string; n: number; total: string }[];
  pending: Record<string, string>;
}

// Visão geral da TECHNOVE (seção 3.3): dados consolidados de todos os consultores, com filtros.
export function VisaoGeralPage() {
  const [consultantId, setConsultantId] = useState('');
  const [clientId, setClientId] = useState('');
  const consultants = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  const clients = useQuery({ queryKey: ['clients', 'all-min'], queryFn: () => api.get<Paged<Client>>('/clients?pageSize=200') });
  const q = useQuery({
    queryKey: ['overview', consultantId, clientId],
    queryFn: () => api.get<CompanyOverview>(`/overview${qs({ consultantId, clientId })}`),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Visão geral da TECHNOVE</h1>
          <p>Consolidado de todos os consultores.</p>
        </div>
      </div>
      <div className="toolbar">
        <label className="visually-hidden" htmlFor="f-consultant">Responsável</label>
        <select id="f-consultant" value={consultantId} onChange={(e) => setConsultantId(e.target.value)}>
          <option value="">Todos os responsáveis</option>
          {consultants.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <label className="visually-hidden" htmlFor="f-client">Cliente</label>
        <select id="f-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Todos os clientes</option>
          {clients.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.tradeName || c.name}
            </option>
          ))}
        </select>
      </div>

      {q.isLoading && <Loading />}
      <ErrorAlert error={q.error} />
      {q.data && (
        <>
          <div className="grid grid-4 grid-stats" style={{ marginBottom: '1rem' }}>
            <div className="stat">
              <div className="label">Clientes ativos</div>
              <div className="value">{q.data.clients.active ?? 0}</div>
            </div>
            <div className="stat">
              <div className="label">Projetos ativos</div>
              <div className="value">{q.data.projectsByStatus.ativo ?? 0}</div>
            </div>
            <div className="stat">
              <div className="label">Projetos pausados</div>
              <div className="value">{q.data.projectsByStatus.pausado ?? 0}</div>
            </div>
            <div className="stat">
              <div className="label">Contratos ativos</div>
              <div className="value">{Object.values(q.data.activeContractsByModality).reduce((a, b) => a + (b ?? 0), 0)}</div>
            </div>
          </div>

          <div className="grid grid-2">
            <section className="card">
              <h2>Projetos ativos por responsável</h2>
              <table className="table">
                <thead>
                  <tr>
                    <th>Consultor</th>
                    <th style={{ textAlign: 'right' }}>Projetos</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.projectsByOwner.map((r) => (
                    <tr key={r.consultantId}>
                      <td>{r.name}</td>
                      <td style={{ textAlign: 'right' }}>{r.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <section className="card">
              <h2>Contratos ativos por modalidade</h2>
              <table className="table">
                <tbody>
                  {(Object.keys(MODALITY_LABEL) as (keyof typeof MODALITY_LABEL)[]).map((m) => (
                    <tr key={m}>
                      <td>{MODALITY_LABEL[m]}</td>
                      <td style={{ textAlign: 'right' }}>{q.data.activeContractsByModality[m] ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>

          <div className="grid grid-2">
            <section className="card">
              <h2>Produção confirmada — {monthLabel(`${q.data.production.month}-01`)}</h2>
              <p className="small muted">Execuções confirmadas e não anuladas, pela data do serviço, atribuídas a quem executou. Rascunhos e pendências: {q.data.openDrafts}.</p>
              {q.data.production.byExecutor.length === 0 ? (
                <p className="muted">Nenhuma execução confirmada no mês.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Executor</th>
                      <th style={{ textAlign: 'right' }}>Horas</th>
                      <th style={{ textAlign: 'right' }}>Sessões</th>
                      <th style={{ textAlign: 'right' }}>Clientes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.production.byExecutor.map((r) => (
                      <tr key={r.consultantId}>
                        <td>{r.name}</td>
                        <td style={{ textAlign: 'right' }}>{hm(r.minutes)}</td>
                        <td style={{ textAlign: 'right' }}>{r.sessions}</td>
                        <td style={{ textAlign: 'right' }}>{r.clientsServed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
            <section className="card">
              <h2>Agenda dos próximos 7 dias</h2>
              {q.data.agendaWeek.length === 0 ? (
                <p className="muted">Nenhum compromisso.</p>
              ) : (
                <div className="list">
                  {q.data.agendaWeek.slice(0, 15).map((a) => (
                    <AppointmentItem key={a.id} a={a} />
                  ))}
                </div>
              )}
            </section>
          </div>
          <div className="grid grid-2">
            <section className="card">
              <div className="card-head">
                <h2>Saldos dos bancos de horas</h2>
                <Link to="/bancos-de-horas" className="small">Ver todos</Link>
              </div>
              {q.data.balances.length === 0 ? (
                <p className="muted">Nenhum banco ativo.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Cliente / contrato</th>
                      <th style={{ textAlign: 'right' }}>Consumido</th>
                      <th style={{ textAlign: 'right' }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.balances.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <Link to={`/bancos-de-horas/${b.id}`}>{b.clientName}</Link> <span className="small muted">{b.code}</span>
                          {b.pendingOverageMinutes > 0 && <span className="badge badge-demo">excedente {hm(b.pendingOverageMinutes)}</span>}
                          {!b.hasInitialCredit && <span className="badge badge-demo">sem crédito inicial</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>{hm(b.consumed - b.reversed)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{hm(b.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
            <section className="card">
              <div className="card-head">
                <h2>Faturáveis</h2>
                <Link to="/faturamento" className="small">Abrir faturamento</Link>
              </div>
              <table className="table">
                <tbody>
                  {(
                    [
                      ['Pendente de conferência', (r: { billingStatus: string; transportStatus: string; reviewStatus: string }) => r.billingStatus === 'nao_faturado' && r.transportStatus === 'nao_enviado' && r.reviewStatus === 'pendente_conferencia'],
                      ['Pronto para envio', (r: { billingStatus: string; transportStatus: string; reviewStatus: string }) => r.billingStatus === 'nao_faturado' && r.transportStatus === 'nao_enviado' && r.reviewStatus === 'pronto_envio'],
                      ['Faturado', (r: { billingStatus: string }) => r.billingStatus === 'faturado' || r.billingStatus === 'ajustado'],
                    ] as const
                  ).map(([label, pred]) => {
                    const rows = q.data!.billables.filter((r) => pred(r as never));
                    const total = rows.reduce((s, r) => s + Math.round(Number(r.total) * 100), 0);
                    return (
                      <tr key={label}>
                        <td>
                          {label} <span className="small muted">({rows.reduce((s, r) => s + r.n, 0)})</span>
                        </td>
                        <td style={{ textAlign: 'right' }}>{money((total / 100).toFixed(2))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="small muted">Faturado não significa recebido; recebimentos ficam no aplicativo financeiro.</p>
            </section>
          </div>
        </>
      )}
    </>
  );
}
